import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, Subject, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  WalletDto,
  PagedAndSortedRequest,
  CreateMerchantWalletDto,
  UpdateMerchantWalletDto,
  WalletFilterParams,
  CreateSystemWalletDto,
  UpdateSystemWalletDto,
  WhitelistedWalletType,
  WalletLifecycleStateEnum,
  EligibleSweepWalletDto,
} from '@olv/ui-shared/models/wallet.model';
import { TransactionFilter, PagedMerchantTransactionResult } from '@olv/ui-shared/models/merchant-transaction.model';
import {
  WhitelistedWalletFilterParams,
  PagedWhitelistedWalletsResultDto,
  WhitelistedWalletResponseDto,
  ValidateResponse,
  WalletCheckResponse,
} from './merchant.service';
import { AUTH_CONFIG, type AuthConfig } from '@olv/ui-shared/features/auth';
import { PagedResultDto } from '@olv/ui-shared/models/paged-result.dto';
import { merchantAccessContext } from './merchant-access';

export interface SystemWhitelistedWalletCreateDto {
  WalletAddress: string;
  blockchain: string;
  name: string;
  whitelistedWalletType: WhitelistedWalletType;
}

export interface SystemWhitelistedWalletValidateBody {
  NetworkType: number;
  WalletAddress: string;
  WalletName: string;
}

const WithdrawalTypeLabelByValue: Record<number, string> = {
  [WhitelistedWalletType.SettlementPayout]: 'Settlement Payout',
  [WhitelistedWalletType.ClientPayout]: 'Client Payout',
  [WhitelistedWalletType.ServicePayout]: 'Service Payout',
};

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  private readonly http = inject(HttpClient);

  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);
  private readonly apiUrl = `${this.cfg.baseUrl}/api/Wallet`;
  private readonly merchantWalletUrl = `${this.cfg.baseUrl}/api/MerchantWallet`;
  private readonly systemWalletUrl = `${this.cfg.baseUrl}/api/SystemWallet`;
  private readonly whitelistedWalletApiUrl = `${this.cfg.baseUrl}/api/SystemWhitelistedWallet`;
  private readonly walletRefreshSubject = new Subject<void>();
  private readonly walletNameSignal = signal<string>('');

  walletRefresh$ = this.walletRefreshSubject.asObservable();

  getWalletNameSignal(): WritableSignal<string> {
    return this.walletNameSignal;
  }

  setWalletName(name: string): void {
    this.walletNameSignal.set(name);
  }

  triggerSystemWalletRefresh(): void {
    this.walletRefreshSubject.next();
  }

  /**
   * Get all wallets with optional paging and sorting
   * @param request Paging and sorting parameters
   * @returns Observable of paged wallet results
   */
  getWallets(request: PagedAndSortedRequest = { skipCount: 0 }): Observable<PagedResultDto<WalletDto>> {
    // Map camelCase request keys to PascalCase for backend compatibility
    const paramMap: Record<string, string> = {
      skipCount: 'SkipCount',
      maxResultCount: 'MaxResultCount',
      sorting: 'Sorting',
    };
    let params = new HttpParams();
    Object.entries(request).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        const mappedKey = paramMap[key] || key;
        params = params.set(mappedKey, String(value));
      }
    });
    return this.http.get<PagedResultDto<WalletDto>>(this.apiUrl, { params }).pipe(catchError(this.handleError));
  }

  /**
   * Get wallets for a specific merchant with optional filtering
   * @param merchantId The merchant ID
   * @param params Optional filter parameters
   * @returns Observable of paged wallet results
   */
  getMerchantWallets(
    merchantId: string,
    params: WalletFilterParams = { skipCount: 0 },
  ): Observable<PagedResultDto<WalletDto>> {
    if (!merchantId) {
      return throwError(() => new Error('Invalid merchant ID'));
    }

    const url = `${this.merchantWalletUrl}/by-merchant/${merchantId}`;
    // Fired alongside the merchant request, so a disabled merchant's 403 here must not log the user out either
    let httpParams = new HttpParams();

    const paramMap: Record<string, string> = {
      walletType: 'WalletTypes',
      status: 'WalletStatuses',
      blockchain: 'NetworkTypes',
      riskLevel: 'RiskLevels',
      balance: 'WalletBalance',
      lifecycleState: 'LifecycleStates',
      skipCount: 'SkipCount',
      maxResultCount: 'MaxResultCount',
      filter: 'Filter',
      sorting: 'Sorting',
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && key !== 'merchantId') {
        const mappedKey = paramMap[key] || key;

        if (Array.isArray(value)) {
          value.forEach((val) => {
            httpParams = httpParams.append(mappedKey, String(val));
          });
        } else {
          httpParams = httpParams.set(mappedKey, String(value));
        }
      }
    });

    return this.http.get<PagedResultDto<WalletDto>>(url, { params: httpParams, context: merchantAccessContext() }).pipe(
      map((response: PagedResultDto<any>) => this.transformWalletResponse(response)),
      catchError((error) => {
        console.error('WalletService: Error fetching merchant wallets:', error);
        return this.handleError(error);
      }),
    );
  }

  /**
   * Get API-created wallets (one-time and dedicated) for a specific merchant
   * @param merchantId The merchant ID
   * @param params Optional filter parameters
   * @returns Observable of paged wallet results
   */
  getMerchantApiWallets(
    merchantId: string,
    params: WalletFilterParams = { skipCount: 0 },
  ): Observable<PagedResultDto<WalletDto>> {
    if (!merchantId) {
      return throwError(() => new Error('Invalid merchant ID'));
    }

    const url = `${this.merchantWalletUrl}/by-merchant/${merchantId}/api-wallets`;
    let httpParams = new HttpParams();

    const paramMap: Record<string, string> = {
      walletType: 'WalletTypes',
      status: 'WalletStatuses',
      blockchain: 'NetworkTypes',
      riskLevel: 'RiskLevels',
      balance: 'WalletBalance',
      lifecycleState: 'LifecycleStates',
      skipCount: 'SkipCount',
      maxResultCount: 'MaxResultCount',
      filter: 'Filter',
      sorting: 'Sorting',
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && key !== 'merchantId') {
        const mappedKey = paramMap[key] || key;

        if (Array.isArray(value)) {
          value.forEach((val) => {
            httpParams = httpParams.append(mappedKey, String(val));
          });
        } else {
          httpParams = httpParams.set(mappedKey, String(value));
        }
      }
    });

    return this.http.get<PagedResultDto<WalletDto>>(url, { params: httpParams, context: merchantAccessContext() }).pipe(
      map((response: PagedResultDto<any>) => this.transformWalletResponse(response)),
      catchError((error) => {
        console.error('WalletService: Error fetching merchant API wallets:', error);
        return this.handleError(error);
      }),
    );
  }

  /**
   * Get wallets for merchant portal
   * @param params Optional filter parameters
   * @returns Observable of paged wallet results
   */
  getMerchantPortalWallets(params: WalletFilterParams = { skipCount: 0 }): Observable<PagedResultDto<WalletDto>> {
    const url = `${this.merchantWalletUrl}`;
    let httpParams = new HttpParams();

    const paramMap: Record<string, string> = {
      walletType: 'WalletTypes',
      status: 'WalletStatuses',
      blockchain: 'NetworkTypes',
      riskLevel: 'RiskLevels',
      balance: 'WalletBalance',
      lifecycleState: 'LifecycleStates',
      skipCount: 'SkipCount',
      maxResultCount: 'MaxResultCount',
      filter: 'Filter',
      sorting: 'Sorting',
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        const mappedKey = paramMap[key] || key;

        if (Array.isArray(value)) {
          value.forEach((val) => {
            httpParams = httpParams.append(mappedKey, String(val));
          });
        } else {
          httpParams = httpParams.set(mappedKey, String(value));
        }
      }
    });

    return this.http.get<PagedResultDto<WalletDto>>(url, { params: httpParams }).pipe(
      map((response: PagedResultDto<any>) => this.transformWalletResponse(response)),
      catchError((error) => {
        console.error('WalletService: Error fetching merchant wallets:', error);
        return this.handleError(error);
      }),
    );
  }

  /**
   * Get system wallets with optional paging and sorting
   * @param request Paging and sorting parameters
   * @returns Observable of paged system wallet results
   */
  getSystemWallets(params: WalletFilterParams = { skipCount: 0 }): Observable<PagedResultDto<WalletDto>> {
    let httpParams = new HttpParams();

    const paramMap: Record<string, string> = {
      walletType: 'WalletTypes',
      systemWalletType: 'SystemWalletTypes',
      status: 'WalletStatuses',
      blockchain: 'NetworkTypes',
      riskLevel: 'RiskLevels',
      balance: 'WalletBalance',
      skipCount: 'SkipCount',
      maxResultCount: 'MaxResultCount',
      searchTerm: 'SearchTerm',
      sorting: 'Sorting',
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        const mappedKey = paramMap[key] || key;

        if (Array.isArray(value)) {
          value.forEach((val) => {
            httpParams = httpParams.append(mappedKey, String(val));
          });
        } else {
          httpParams = httpParams.set(mappedKey, String(value));
        }
      }
    });

    return this.http.get<PagedResultDto<WalletDto>>(this.systemWalletUrl, { params: httpParams }).pipe(
      map((response: PagedResultDto<any>) => this.transformWalletResponse(response)),
      catchError((error) => {
        console.error('WalletService: Error fetching system wallets:', error);
        return this.handleError(error);
      }),
    );
  }

  /**
   * Get system whitelisted wallets with optional filtering
   * @param params Optional filter parameters
   * @returns Observable of paged wallet results
   */
  getSystemWhitelistedWallets(params?: WhitelistedWalletFilterParams): Observable<PagedWhitelistedWalletsResultDto> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.skipCount !== undefined) {
        httpParams = httpParams.set('SkipCount', params.skipCount.toString());
      }
      if (params.maxResultCount !== undefined) {
        httpParams = httpParams.set('MaxResultCount', params.maxResultCount.toString());
      }
      if (params.sorting) {
        httpParams = httpParams.set('Sorting', params.sorting);
      }
      if (params.filter) {
        httpParams = httpParams.set('Filter', params.filter);
      }
      if (params.networkIdFilter) {
        httpParams = httpParams.set('NetworkIdFilter', params.networkIdFilter);
      }
      if (params.networkTypes?.length) {
        params.networkTypes.forEach((val) => (httpParams = httpParams.append('NetworkTypes', val.toString())));
      }
      if (params.walletStatuses?.length) {
        params.walletStatuses.forEach((val) => (httpParams = httpParams.append('WalletStatuses', val.toString())));
      }
      if (params.whitelistedWalletTypes?.length) {
        params.whitelistedWalletTypes.forEach(
          (val) => (httpParams = httpParams.append('WhitelistedWalletTypes', val.toString())),
        );
      }
      if (params.riskLevels?.length) {
        params.riskLevels.forEach((val) => (httpParams = httpParams.append('RiskLevels', val.toString())));
      }
    }
    return this.http
      .get<PagedWhitelistedWalletsResultDto>(this.whitelistedWalletApiUrl, {
        params: httpParams,
      })
      .pipe(
        map((response) => ({
          totalCount: response.totalCount,
          items: response.items.map((item) => ({
            ...item,
            // Optionally normalize here
          })),
        })),
      );
  }

  /**
   * Add a new whitelisted wallet
   * @param walletData Wallet data to add
   * @param documentFile Optional document file to upload
   * @returns Observable of the created whitelisted wallet
   */
  addSystemWhitelistedWallet(
    walletData: SystemWhitelistedWalletCreateDto,
    totp: string,
    documentFile?: File,
  ): Observable<WhitelistedWalletResponseDto> {
    const formData = new FormData();

    formData.append('WalletAddress', walletData.WalletAddress);
    formData.append('NetworkType', walletData.blockchain);
    formData.append('name', walletData.name);
    formData.append('totp', totp);
    formData.append('WhitelistedWalletType', String(walletData.whitelistedWalletType));

    if (documentFile) {
      formData.append('documentFile', documentFile, documentFile.name);
    }

    return this.http.post<WhitelistedWalletResponseDto>(this.whitelistedWalletApiUrl, formData).pipe(
      catchError((error) => {
        console.error(error); // Log the full error object
        return throwError(() => error);
      }),
    );
  }

  validateWhitelistedWallet(walletData: SystemWhitelistedWalletValidateBody): Observable<ValidateResponse | void> {
    const url = `${this.whitelistedWalletApiUrl}/validate`;
    return this.http.post<ValidateResponse | void>(url, walletData).pipe(catchError((err) => throwError(() => err)));
  }

  /**
   * Updates the status of a whitelisted wallet in the system based on the specified action.
   *
   * @param {string} id - The unique identifier of the wallet to update.
   * @param {'approve' | 'reject' | 'revoke'} action - The action to be performed on the wallet's status.
   * @param {string} [comments] - An optional comment providing additional context for the action.
   * @param {string} [otp] - An optional one-time password for authentication, if required.
   * @return {Observable<void>} An observable that completes when the operation is successful.
   */
  updateSystemWhitelistedWalletStatus(
    id: string,
    action: 'approve' | 'reject' | 'revoke',
    comments?: string,
    otp?: string,
  ): Observable<void> {
    const url = `${this.whitelistedWalletApiUrl}/${id}/${action}`;
    const body = {
      comments: comments,
      totp: otp,
    };
    return this.http.put<void>(url, body).pipe(
      catchError((error) => {
        console.error(error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get a wallet by ID
   * @param id Wallet ID
   * @returns Observable of wallet
   */
  getWallet(id: string): Observable<WalletDto> {
    return this.http.get<WalletDto>(`${this.apiUrl}/${id}`).pipe(
      map((wallet) => this.transformWalletData(wallet)),
      catchError(this.handleError),
    );
  }

  /**
   * Get a merchant wallet by ID
   * @param id Wallet ID
   * @returns Observable of wallet
   */
  getMerchantWallet(id: string): Observable<WalletDto> {
    return this.http.get<WalletDto>(`${this.merchantWalletUrl}/${id}`).pipe(
      map((wallet) => this.transformWalletData(wallet)),
      catchError(this.handleError),
    );
  }

  /**
   * Get a system wallet by ID
   * @param id Wallet ID
   * @returns Observable of wallet
   */
  getSystemWallet(id: string): Observable<WalletDto> {
    return this.http.get<WalletDto>(`${this.systemWalletUrl}/${id}`).pipe(
      map((wallet) => this.transformWalletData(wallet)),
      catchError(this.handleError),
    );
  }

  /**
   * Get a wallet by name
   * @param name Wallet name
   * @returns Observable of wallet
   */
  getWalletByName(name: string): Observable<WalletDto> {
    return this.http.get<WalletDto>(`${this.apiUrl}/by-name/${name}`).pipe(catchError(this.handleError));
  }

  /**
   * Create a new wallet
   * @param wallet Wallet data
   * @returns Observable of created wallet
   */
  createWallet(wallet: { name: string; networkType: number }): Observable<WalletDto> {
    return this.http.post<WalletDto>(this.apiUrl, wallet).pipe(catchError(this.handleError));
  }

  /**
   * Create a new merchant wallet
   * @param wallet Merchant wallet data
   * @returns Observable of created wallet
   */
  createMerchantWallet(wallet: CreateMerchantWalletDto): Observable<WalletDto> {
    // Transform the DTO to match backend property naming (PascalCase)
    const backendDto = {
      AddressFamily: Number(wallet.addressFamily),
      MerchantId: wallet.merchantId,
      WalletType: wallet.walletType,
      Name: wallet.name || '',
    };

    return this.http.post<WalletDto>(this.merchantWalletUrl, backendDto).pipe(catchError(this.handleError));
  }

  generateMerchantDepositWallet(merchantId: string, networkType: number): Observable<WalletDto> {
    const backendDto = {
      MerchantId: merchantId,
      NetworkType: Number(networkType),
    };

    return this.http
      .post<WalletDto>(`${this.merchantWalletUrl}/generate-deposit-otw`, backendDto)
      .pipe(catchError(this.handleError));
  }

  generateSystemDepositWallet(networkType: number): Observable<WalletDto> {
    const backendDto = {
      NetworkType: Number(networkType),
    };

    return this.http
      .post<WalletDto>(`${this.systemWalletUrl}/generate-deposit-otw`, backendDto)
      .pipe(catchError(this.handleError));
  }

  /**
   * Create a new System wallet
   * @param wallet System wallet data
   * @returns Observable of created wallet
   */
  createSystemWallet(wallet: CreateSystemWalletDto): Observable<WalletDto> {
    // Transform the DTO to match backend property naming (PascalCase)
    const backendDto = {
      AddressFamily: Number(wallet.addressFamily),
      WalletType: wallet.walletType,
      SystemWalletType: wallet.systemWalletType,
      Name: wallet.name,
    };

    return this.http.post<WalletDto>(this.systemWalletUrl, backendDto).pipe(catchError(this.handleError));
  }

  /**
   * Update an existing wallet
   * @param id Wallet ID
   * @param wallet Updated wallet data
   * @returns Observable of updated wallet
   */
  updateWallet(id: string, wallet: { name: string; networkType: number }): Observable<WalletDto> {
    return this.http.put<WalletDto>(`${this.apiUrl}/${id}`, wallet).pipe(catchError(this.handleError));
  }

  /**
   * Update a merchant wallet (primarily for status changes)
   * @param id Wallet ID
   * @param wallet Updated merchant wallet data
   * @returns Observable of updated wallet
   */
  updateSystemWallet(id: string, wallet: UpdateSystemWalletDto): Observable<WalletDto> {
    // Transform the DTO to match backend property naming (PascalCase)
    const backendDto = {
      WalletId: wallet.walletId,
      WalletName: wallet.walletName,
    };

    return this.http.put<WalletDto>(`${this.systemWalletUrl}/${id}`, backendDto).pipe(catchError(this.handleError));
  }

  /**
   * Update a merchant wallet (primarily for status changes)
   * @param id Wallet ID
   * @param wallet Updated merchant wallet data
   * @returns Observable of updated wallet
   */
  updateMerchantWallet(id: string, wallet: UpdateMerchantWalletDto): Observable<WalletDto> {
    // Transform the DTO to match backend property naming (PascalCase)
    const backendDto = {
      WalletId: wallet.walletId,
      MerchantId: wallet.merchantId,
      WalletName: wallet.walletName,
    };

    return this.http.put<WalletDto>(`${this.merchantWalletUrl}/${id}`, backendDto).pipe(catchError(this.handleError));
  }

  /**
   * Get transaction history for a specific system wallet with pagination
   * @param walletId System wallet ID
   * @param filter Optional filter parameters
   */
  getSystemWalletTransactions(
    walletId: string,
    filter?: TransactionFilter,
  ): Observable<PagedMerchantTransactionResult> {
    const url = `${this.systemWalletUrl}/${walletId}/transactions`;
    let params = new HttpParams();

    if (filter) {
      if (filter.skipCount !== undefined) {
        params = params.set('SkipCount', filter.skipCount.toString());
      }

      if (filter.maxResultCount !== undefined) {
        params = params.set('MaxResultCount', filter.maxResultCount.toString());
      }

      if (filter.filter) {
        params = params.set('Filter', filter.filter);
      }

      if (filter.sorting) {
        params = params.set('Sorting', filter.sorting);
      }

      if (filter.account) {
        params = params.set('Account', filter.account);
      }

      if (filter.actionRequired !== undefined) {
        filter.actionRequired.forEach((action) => (params = params.append('ActionRequired', action.toString())));
      }

      if (filter.transactionType?.length) {
        filter.transactionType.forEach((type) => (params = params.append('TransactionType', type.toString())));
      }

      if (filter.status?.length) {
        filter.status.forEach((status) => (params = params.append('TransactionStatus', status.toString())));
      }

      if (filter.blockchain?.length) {
        filter.blockchain.forEach((network) => (params = params.append('Blockchain', network.toString())));
      }

      if (filter.riskLevel?.length) {
        filter.riskLevel.forEach((level) => (params = params.append('RiskLevel', level.toString())));
      }

      if (filter.exchangeCategories?.length) {
        filter.exchangeCategories.forEach((category) => (params = params.append('ExchangeCategories', category)));
      }

      if (filter.exchangeRateOverridden !== undefined) {
        params = params.set('ExchangeRateOverridden', filter.exchangeRateOverridden.toString());
      }
    }

    return this.http.get<PagedMerchantTransactionResult>(url, { params }).pipe(
      map((response) => {
        const transformedItems = response.items.map((item) => ({
          ...item,
          date: new Date(item.date),
        }));

        return {
          ...response,
          items: transformedItems,
        };
      }),
      catchError((error) => this.handleError(error)),
    );
  }

  /**
   * Get transaction history for a specific Merchant wallet with pagination
   * @param walletId Merchant wallet ID
   * @param filter Optional filter parameters
   */
  getMerchantWalletTransactions(
    walletId: string,
    filter?: TransactionFilter,
  ): Observable<PagedMerchantTransactionResult> {
    const url = `${this.merchantWalletUrl}/${walletId}/transactions`;
    let params = new HttpParams();

    if (filter) {
      if (filter.skipCount !== undefined) {
        params = params.set('SkipCount', filter.skipCount.toString());
      }

      if (filter.maxResultCount !== undefined) {
        params = params.set('MaxResultCount', filter.maxResultCount.toString());
      }

      if (filter.filter) {
        params = params.set('Filter', filter.filter);
      }

      if (filter.sorting) {
        params = params.set('Sorting', filter.sorting);
      }

      if (filter.account) {
        params = params.set('Account', filter.account);
      }

      if (filter.actionRequired !== undefined) {
        filter.actionRequired.forEach((action) => (params = params.append('ActionRequired', action.toString())));
      }

      if (filter.transactionType?.length) {
        filter.transactionType.forEach((type) => (params = params.append('TransactionType', type.toString())));
      }

      if (filter.status?.length) {
        filter.status.forEach((status) => (params = params.append('TransactionStatus', status.toString())));
      }

      if (filter.blockchain?.length) {
        filter.blockchain.forEach((network) => (params = params.append('Blockchain', network.toString())));
      }

      if (filter.riskLevel?.length) {
        filter.riskLevel.forEach((level) => (params = params.append('RiskLevel', level.toString())));
      }

      if (filter.exchangeCategories?.length) {
        filter.exchangeCategories.forEach((category) => (params = params.append('ExchangeCategories', category)));
      }

      if (filter.exchangeRateOverridden !== undefined) {
        params = params.set('ExchangeRateOverridden', filter.exchangeRateOverridden.toString());
      }
    }

    return this.http.get<PagedMerchantTransactionResult>(url, { params }).pipe(
      map((response) => {
        const transformedItems = response.items.map((item) => ({
          ...item,
          date: new Date(item.date),
        }));

        return {
          ...response,
          items: transformedItems,
        };
      }),
      catchError((error) => this.handleError(error)),
    );
  }

  checkMerchantWallet(walletAddress: string): Observable<WalletCheckResponse> {
    const url = `${this.apiUrl}/check-wallet-ownership`;
    const params = new HttpParams().set('WalletAddress', walletAddress);

    return this.http.get<WalletCheckResponse>(url, { params }).pipe(
      catchError(() => {
        const message = `Failed to check wallet.`;
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Update the status of a merchant wallet
   * @param id wallet ID
   * @param active Current active status
   */
  updateMerchantWalletStatus(id: string, active: boolean): Observable<void> {
    const url = active ? `${this.merchantWalletUrl}/${id}/disable` : `${this.merchantWalletUrl}/${id}/enable`;
    return this.http.put<void>(url, null).pipe(
      catchError((error) => {
        console.error(error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Update the status of a system wallet
   * @param id wallet ID
   * @param active Current active status
   */
  updateSystemWalletStatus(id: string, active: boolean): Observable<void> {
    const url = active ? `${this.systemWalletUrl}/${id}/disable` : `${this.systemWalletUrl}/${id}/enable`;
    return this.http.put<void>(url, null).pipe(
      catchError((error) => {
        console.error(error);
        return throwError(() => error);
      }),
    );
  }
  /**
   * @param walletId Source wallet ID (
   * @param payload  SweepWalletRequestDto
   * @param type Wallet type: system | merchant
   */
  sweepWallet(
    walletId: string,
    payload: {
      targetWalletId: string;
      currencyIds: string[];
      totp: string;
    },
    type: 'system' | 'merchant',
  ): Observable<void> {
    if (!walletId) {
      return throwError(() => new Error('WalletId is required'));
    }

    const baseUrl = type === 'merchant' ? this.merchantWalletUrl : this.systemWalletUrl;

    return this.http.post<void>(`${baseUrl}/${walletId}/sweep`, payload).pipe(
      catchError((error) => {
        console.error(`WalletService: Error sweeping ${type} wallet`, error);
        return this.handleError(error);
      }),
    );
  }

  getMerchantEligibleSweepWallets(walletId: string, merchantId: string): Observable<EligibleSweepWalletDto[]> {
    if (!walletId || !merchantId) {
      return throwError(() => new Error('Invalid wallet or merchant ID'));
    }

    const url = `${this.merchantWalletUrl}/${walletId}/by-merchant/${merchantId}/eligible-sweep-wallets`;
    return this.http.get<EligibleSweepWalletDto[]>(url).pipe(catchError(this.handleError));
  }

  getSystemEligibleSweepWallets(walletId: string): Observable<EligibleSweepWalletDto[]> {
    if (!walletId) {
      return throwError(() => new Error('Invalid wallet ID'));
    }

    const url = `${this.systemWalletUrl}/${walletId}/eligible-sweep-wallets`;
    return this.http.get<EligibleSweepWalletDto[]>(url).pipe(catchError(this.handleError));
  }

  moveNonSpendableFunds(walletId: string, totp: string): Observable<void> {
    if (!walletId) {
      return throwError(() => new Error('WalletId is required'));
    }

    return this.http.post<void>(`${this.merchantWalletUrl}/${walletId}/move-non-spendable-funds`, { totp }).pipe(
      catchError((error) => {
        console.error('WalletService: Error moving non-spendable funds of merchant wallet', error);
        return this.handleError(error);
      }),
    );
  }

  updateWalletSweepingStatus(params: {
    action: string;
    type: string;
    walletId: string;
    totp: string;
  }): Observable<void> {
    const { action, type, walletId, totp } = params;
    const baseUrl = type === 'merchant' ? this.merchantWalletUrl : this.systemWalletUrl;

    const url = `${baseUrl}/${walletId}/${action}-sweeping`;

    return this.http.put<void>(url, { totp }).pipe(
      catchError((error) => {
        console.error('WalletService: Error updating sweeping status', error);
        return throwError(() => error);
      }),
    );
  }
  /**
   * Transform wallet data from backend format to frontend model
   * @param wallet Wallet data from backend
   * @returns Transformed wallet data
   */
  /**
   * Transform wallet data from backend format to frontend model
   * Handles secure wallet response format without private key information
   * @param wallet Wallet data from backend
   * @returns Transformed wallet data
   */
  private transformWalletData(wallet: any): WalletDto {
    if (!wallet) return wallet;
    let normalizedWithdrawalType: string | undefined;
    if (wallet.withdrawalType !== undefined && wallet.withdrawalType !== null) {
      if (typeof wallet.withdrawalType === 'string') {
        normalizedWithdrawalType = wallet.withdrawalType;
      } else {
        const asNumber = Number(wallet.withdrawalType);
        normalizedWithdrawalType = WithdrawalTypeLabelByValue[asNumber] ?? String(wallet.withdrawalType);
      }
    }
    // Start with base mapped object
    const mapped: any = {
      ...wallet,
      // Ensure dates are properly handled
      createdAt: wallet.createdAt ? new Date(wallet.createdAt) : new Date(),
      updatedAt: wallet.updatedAt && wallet.updatedAt !== '0001-01-01T00:00:00' ? new Date(wallet.updatedAt) : null,

      // Ensure enum values are properly converted
      networkType: Number(wallet.networkType),
      ownerType: Number(wallet.ownerType),
      walletType: wallet.walletType !== undefined ? Number(wallet.walletType) : undefined,
      status: Number(wallet.status),

      // Handle risk assessment data
      riskLevel: wallet.riskLevel !== undefined ? Number(wallet.riskLevel) : undefined,
      riskScore: wallet.riskScore,
      lifecycleState: this.parseLifecycleState(wallet.lifecycleState),

      // Handle withdrawal type for one-time wallets
      withdrawalType: normalizedWithdrawalType,

      // Handle nullable fields
      merchantName: wallet.merchantName || null,
      systemWalletType: wallet.systemWalletType !== null ? Number(wallet.systemWalletType) : null,
      publicKey: wallet.publicKey || null,

      // Process balances if available
      balance: wallet.balance,
      address: wallet.address ?? wallet.walletAddress ?? null,
    };

    // Only include nonce if backend provided a value (avoid NaN/undefined on objects)
    if (wallet.nonce !== null && wallet.nonce !== undefined) {
      const num = Number(wallet.nonce);
      // Include only if it's a valid number
      if (!Number.isNaN(num)) {
        mapped.nonce = num;
      }
    }

    return mapped as WalletDto;
  }

  /**
   * Transform paged wallet response
   * @param response Paged response from backend
   * @returns Transformed paged response
   */
  private parseLifecycleState(value: unknown): WalletLifecycleStateEnum | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    if (typeof value === 'number') return value;
    switch (String(value).trim().toLowerCase()) {
      case 'serving':
        return WalletLifecycleStateEnum.Serving;
      case 'active':
        return WalletLifecycleStateEnum.Active;
      case 'capped':
        return WalletLifecycleStateEnum.Capped;
      case 'closed':
        return WalletLifecycleStateEnum.Closed;
      case 'queued':
        return WalletLifecycleStateEnum.Queued;
      default:
        return undefined;
    }
  }

  private transformWalletResponse(response: PagedResultDto<any>): PagedResultDto<WalletDto> {
    if (!response) return response;

    return {
      ...response,
      items: response.items.map((wallet) => this.transformWalletData(wallet)),
    };
  }

  /**
   * Handle HTTP errors
   * @param error HTTP error response
   * @returns Observable with error
   */
  private handleError(error: HttpErrorResponse) {
    console.error('Wallet service error:', error);

    // For validation errors (400), preserve the full error response
    // so the component can access error.error.errors
    if (error.status === 400 && error.error?.errors) {
      return throwError(() => error);
    }

    // For other errors, create a simplified error with message
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error. Both sweep endpoints answer with a bare string body (BadRequest("..."), NotFound("...")),
      // which has no .message, so unwrapping it here is what lets every consumer show the server's own reason.
      errorMessage =
        (typeof error.error === 'string' && error.error.trim()) ||
        error.error?.message ||
        `Error Code: ${error.status}, Message: ${error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }
}
